import * as THREE from 'three';
import * as SceneModule from '../scene.js';
import { pointMaterial } from './material.js';

// Tile state
export const state = { 
  tileSize: 44, 
  tilesX: 5, 
  tilesZ: 4, 
  scrollSpeed: 3.0 
};

export const scrollOffset = new THREE.Vector2(0, 0);

// Tiles array
let tilesPoints = [];

export function getTilesPoints() {
  return tilesPoints;
}

export { tilesPoints };

// Segments for geometry (base resolution)
let segments = 256;

export function setSegments(newSegments) {
  segments = newSegments;
}

export function getSegments() {
  return segments;
}

// LOD System
// Define LOD levels: [segments, maxDistance]
// Tiles closer than maxDistance use this resolution
const LOD_LEVELS = [
  { segments: 256, maxDistance: 50 },   // High detail for near tiles
  { segments: 128, maxDistance: 90 },   // Medium detail
  { segments: 64, maxDistance: Infinity } // Low detail for far tiles
];

// Cache geometries for each LOD level to avoid recreation
const lodGeometries = new Map();

// Track current LOD level per tile
const tileLodLevels = [];

// Reusable vector for distance calculations
const _tileCenter = new THREE.Vector3();

function buildGeometryForLOD(lodSegments) {
  // Check cache first
  if (lodGeometries.has(lodSegments)) {
    return lodGeometries.get(lodSegments);
  }
  
  const g = new THREE.PlaneGeometry(state.tileSize, state.tileSize, lodSegments, lodSegments);
  g.rotateX(-Math.PI / 2);
  g.deleteAttribute('normal');
  g.deleteAttribute('tangent');
  // Convert to non-indexed for point rendering
  const nonIndexed = g.toNonIndexed();
  nonIndexed.computeBoundingSphere();
  
  // Cache for reuse
  lodGeometries.set(lodSegments, nonIndexed);
  
  return nonIndexed;
}

function buildBaseGeometry() {
  return buildGeometryForLOD(segments);
}

// Get appropriate LOD level based on distance from camera
function getLodLevelForDistance(distance) {
  for (let i = 0; i < LOD_LEVELS.length; i++) {
    if (distance < LOD_LEVELS[i].maxDistance) {
      return i;
    }
  }
  return LOD_LEVELS.length - 1;
}

// Update LOD for all tiles based on camera position
export function updateTileLOD() {
  const { camera } = SceneModule;
  if (!camera || !tilesPoints || tilesPoints.length === 0) return;
  
  for (let i = 0; i < tilesPoints.length; i++) {
    const tile = tilesPoints[i];
    if (!tile) continue;
    
    // Get tile center in world space
    _tileCenter.copy(tile.position);
    
    // Calculate distance from camera to tile center
    const distance = camera.position.distanceTo(_tileCenter);
    
    // Determine appropriate LOD level
    const newLodLevel = getLodLevelForDistance(distance);
    const currentLodLevel = tileLodLevels[i];
    
    // Only swap geometry if LOD level changed
    if (newLodLevel !== currentLodLevel) {
      const lodSegments = LOD_LEVELS[newLodLevel].segments;
      const newGeometry = buildGeometryForLOD(lodSegments);
      
      // Swap geometry (shared geometry, no disposal needed)
      tile.geometry = newGeometry;
      tileLodLevels[i] = newLodLevel;
    }
  }
}

function positiveModulo(v, m) { 
  return ((v % m) + m) % m; 
}

export function updateTileMatrices() {
  if (!tilesPoints || tilesPoints.length === 0) return;
  let idx = 0;
  const offsetX = (state.tilesX - 1) * state.tileSize * 0.5;
  const offsetZ = (state.tilesZ - 1) * state.tileSize * 0.5;
  const modX = positiveModulo(scrollOffset.x, state.tileSize);
  const modZ = positiveModulo(scrollOffset.y, state.tileSize);
  for (let z = 0; z < state.tilesZ; z++) {
    for (let x = 0; x < state.tilesX; x++) {
      const px = (x * state.tileSize) - offsetX - modX;
      const pz = (z * state.tileSize) - offsetZ - modZ;
      const pts = tilesPoints[idx++];
      if (pts) pts.position.set(px, 0, pz);
    }
  }
}

// Clear cached LOD geometries (call when tile size changes)
export function clearLodCache() {
  lodGeometries.clear();
}

export function rebuildTiling() {
  const { scene } = SceneModule;
  if (tilesPoints && tilesPoints.length) {
    for (const p of tilesPoints) {
      scene.remove(p);
      // Don't dispose shared LOD geometries here
    }
  }
  tilesPoints = [];
  tileLodLevels.length = 0;
  
  // Clear LOD cache when rebuilding (tile size may have changed)
  clearLodCache();
  
  // Start with highest detail geometry, LOD will adjust on first update
  const geometry = buildGeometryForLOD(LOD_LEVELS[0].segments);
  
  for (let z = 0; z < state.tilesZ; z++) {
    for (let x = 0; x < state.tilesX; x++) {
      const pts = new THREE.Points(geometry, pointMaterial);
      // Enable frustum culling for performance
      pts.frustumCulled = true;
      tilesPoints.push(pts);
      tileLodLevels.push(0); // Start at highest LOD level
      scene.add(pts);
    }
  }
  updateTileMatrices();
}
